import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getPlantDetails,
  getSheetColumns,
  getProceedToAnalysis,
  getPlantReadiness,
} from "../../network/cipAPI";
import {
  storeFileId,
  storeApp,
  storeDefaultInformation,
  storeCleanData,
  storeUnits,
  storePlantData,
  storePlantDetails,
  storeTrainChemicalSelection,
} from "../../Store/TemplateStore/template.action";
import Header from "../../components/Header";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import {
  SettingsIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from "../../assets/icons";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChanges";
import LeftPanel from "./LeftPanel";
import TrainTabsSection from "./TrainTabsSection";
import {
  isTrainReadyForAnalysis,
  mergeSelectedTrainReadiness,
} from "./readiness.utils";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import {
  extractPlantId,
  getSelectedPlantTrainInfo,
  getDatasetDetails,
  updatePlantDataTrain,
} from "../../utils/Utils";
import { removeLocalStorageItem } from "../../utils/session";
import ColumnSelection from "./columnSelection";
import ReferenceCondition from "./referenceCondition";
import DefaultCIPInformation from "../defaultCIPInformation";
import {
  DEFAULT_CIP_KEYS,
  NORMALIZATION_TYPES,
  CustomModalButtons,
  DEFAULT_RADIO_COLUMNS,
  FILE_SHEET_MAPPING_COLUMN_TYPES,
} from "../../constants/global.constants";
import ConfirmationPopup from "../../packages/ConfirmationPopup";
import { API_URLS } from "../../network/appUrls";
import FileSheetSelection from "./FileSheetSelection";
import CIPSpecification from "../../components/CIPSpecification";
import DataAppend from "../../components/DataAppend/DataAppned";
import PlantChecklist from "../../components/PlantChecklist";
import Footer from "../../common/footer/Footer";
import CircularLoader from "../../common/styles/components/circularLoader";
import EmptyStateContent from "./EmptyStateContent";
import AdvancedSettings from "./AdvancedSettings/AdvancedSettings";

import "./index.scss";

const basePath = API_URLS.basePath;
const initialMandatoryComponentsStatus = {
  columnMapping: {
    fulfilled: false,
    changed: false,
    error: false,
  },
  referenceConditions: {
    fulfilled: false,
    changed: false,
    error: false,
  },
  defaultCIPInfo: {
    fulfilled: false,
    changed: false,
    error: false,
  },
};

const AnalyseData = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const data = location.state;
  const searchParams = new URLSearchParams(window.location.search);
  const isEdit = searchParams.get("isEdit");
  const showRecurrent = data?.showRecurrent || false;
  const { token, plantData, fileSheetMapping } = useSelector((state) => state);
  const [columnOptions, setColumnOptions] = useState("");
  const [fileId, setFileId] = useState("");
  const [mappedColumn, setMappedColumn] = useState([]);
  const [config, setConfig] = useState([]);
  const [mappedReference, setMappedReference] = useState([]);
  const [tempReferenceValues, setTempReferenceValues] = useState({});
  const [datasetDetails, setDatasetDetails] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileInfo, setFileInfo] = useState([]);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [, setIsNormalizationColumnFilled] = useState(false);
  const [isFTNormFallbackDialogOpen, setIsFTNormFallbackDialogOpen] =
    useState(false);
  const [normSelectionType, setNormSelectionType] = useState(null);
  const [activeLoadingRequests, setActiveLoadingRequests] = useState(0);
  const [sheetNames, setSheetNames] = useState([]);
  const [totalTrainCount, setTotalTrainCount] = useState(null);
  const [selectedColumnRadioColumn, setSelectedColumnRadioColumn] = useState(
    DEFAULT_RADIO_COLUMNS,
  );
  const plantId = extractPlantId();
  const sel_train = getSelectedPlantTrainInfo(plantData, plantId);
  const [selectedTrain, setSelectedTrain] = useState(sel_train || 1);
  const [selectedSheet, setSelectedSheet] = useState();
  const [selectedCIPFileName, setSelectedCIPFileName] = useState(null);
  const [appendFileList, setAppendFileList] = useState([]);
  const [isAppendFile, setIsAppendFile] = useState(null);
  const [appendStatus, setAppendStatus] = useState("idle");
  const [hasRecurrentCIPs, setHasRecurrentCIPs] = useState(false);
  const [ownerUserName, setOwnerUserName] = useState("");
  const [mandatoryComponentsStatus, setMandatoryComponentsStatus] = useState(
    initialMandatoryComponentsStatus,
  );
  const [showPlantChecklist, setShowPlantChecklist] = useState(false);
  const [plantChecklistData, setPlantChecklistData] = useState([]);
  const [plantReadinessStatus, setPlantReadinessStatus] = useState([]);
  const [colMappingType, setColMappingType] = useState(
    FILE_SHEET_MAPPING_COLUMN_TYPES.manual,
  );
  const [tempSelectedTrain, setTempSelectedTrain] = useState(null);

  const setIsLoading = useCallback((isActive) => {
    setActiveLoadingRequests((prevCount) =>
      Boolean(isActive) ? prevCount + 1 : Math.max(0, prevCount - 1),
    );
  }, []);

  const handleSettingsDialogOpen = () => {
    setIsSettingsDialogOpen(true);
  };
  const handleSettingsDialogClose = () => {
    setIsSettingsDialogOpen(false);
  };

  const updatePlantAndNavigate = () => {
    if (!plantData.some((p) => String(p.plant_id) === String(plantId))) {
      dispatch(
        storePlantData([
          ...plantData,
          { plant_id: plantId, sel_train: selectedTrain },
        ]),
      );
    } else {
      dispatch(
        storePlantData(updatePlantDataTrain(plantData, plantId, selectedTrain)),
      );
    }
    navigate(`${basePath}home?plantId=${plantId}`);
  };

  const onExitClick = async () => {
    navigate(`${basePath}editPlant?plantId=${plantId}&isEdit=true`);
  };

  const hasUnsavedChanges = useMemo(() => {
    return Object.values(mandatoryComponentsStatus).some(
      (status) => status.changed === true,
    );
  }, [mandatoryComponentsStatus]);

  const trainTabActions = useMemo(
    () => ({
      setIsLoading,
      setFileId,
      setMappedColumn,
      setMappedReference,
      setSelectedTrain,
      setSelectedSheet,
      setSelectedCIPFileName,
      setHasRecurrentCIPs,
      setIsAppendFile,
      setAppendFileList,
      setAppendStatus,
      setNormSelectionType,
      setDatasetDetails,
      setSheetNames,
      setColumnOptions,
      setFileInfo,
      setUploadedFiles,
      setColMappingType,
      setTempSelectedTrain,
    }),
    [],
  );

  const mergedTrainReadiness = useMemo(
    () =>
      mergeSelectedTrainReadiness(
        plantReadinessStatus,
        selectedTrain,
        mandatoryComponentsStatus,
      ),
    [plantReadinessStatus, selectedTrain, mandatoryComponentsStatus],
  );

  const trainReadinessMap = useMemo(
    () =>
      mergedTrainReadiness.reduce((acc, trainStatus) => {
        const trainNumber = Number(trainStatus?.train);
        if (Number.isNaN(trainNumber)) {
          return acc;
        }

        acc[trainNumber] = isTrainReadyForAnalysis(trainStatus);
        return acc;
      }, {}),
    [mergedTrainReadiness],
  );

  const getHasIncompleteComponents = () => {
    setPlantChecklistData(mergedTrainReadiness);

    const multiTrainHasIncompleteComponents = mergedTrainReadiness.some(
      (trainStatus) => !isTrainReadyForAnalysis(trainStatus),
    );

    return multiTrainHasIncompleteComponents;
  };

  const onProceedClick = async () => {
    setIsLoading(true);
    try {
      const hasIncompleteComponents = getHasIncompleteComponents();

      if (hasIncompleteComponents) {
        setShowPlantChecklist(true);
      } else {
        const response = await getProceedToAnalysis(fileId, selectedTrain);
        if (response && response.status === 200) {
          const { norm_applied_type: appliedType } = response.data;
          dispatch(
            storeCleanData({
              cleanData: null,
              cleanDate: null,
            }),
          );
          if (appliedType === NORMALIZATION_TYPES.FT_NORM_FALLBACK) {
            setIsFTNormFallbackDialogOpen(true);
          } else {
            updatePlantAndNavigate();
          }
        } else {
          createNotification(
            ERROR_NOTIFICATION,
            response.data.detail.error,
            "",
          );
        }
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFTNormFallbackClose = (action) => {
    setIsFTNormFallbackDialogOpen(false);
    if (action === CustomModalButtons.CONFIRM) {
      updatePlantAndNavigate();
    }
  };

  const getPlantDetail = async () => {
    setIsLoading(true);
    try {
      const response = await getPlantDetails(plantId, true, sel_train);
      if (response && response.data) {
        const {
          file,
          parent_file_name,
          plant_file_id,
          col_mapping,
          sheet_names,
          uploaded_files,
          num_trains,
          bulk_cip_file_name,
          has_recurrent_cips,
          config,
          default_cip,
          ref_condition,
          append_files,
          isAppended,
          setting,
          norm_selection_type,
          owner_username,
          name,
          water_type,
          chemical_selection,
          col_mapping_type,
        } = response.data;

        let file_id, sheet_name, min_date, max_date, uploaded_in_blob;
        if (file) {
          ({ file_id, sheet_name, min_date, max_date, uploaded_in_blob } =
            file);
        }

        const fileInfo = {
          file_id,
          sheetName: sheet_name,
          fileName: parent_file_name,
          plant_file_id,
          uploaded_in_blob,
        };
        dispatch(storeUnits(setting));
        dispatch(
          storePlantDetails({
            plantName: name,
            water_type,
          }),
        );
        dispatch(storeFileId(fileInfo));
        setFileInfo(fileInfo);
        setSelectedSheet(sheet_name);
        setFileId(file_id);
        setMappedColumn(col_mapping);
        setAppendFileList(append_files);
        setIsAppendFile(isAppended);
        isAppended && setAppendStatus("success");
        setSheetNames(sheet_names);
        setUploadedFiles(uploaded_files);
        setTotalTrainCount(num_trains);
        setSelectedTrain(sel_train);
        setSelectedCIPFileName(bulk_cip_file_name);
        setHasRecurrentCIPs(has_recurrent_cips);
        setNormSelectionType(norm_selection_type);
        setConfig(config);
        setMappedReference(ref_condition?.[0]);
        setOwnerUserName(owner_username);
        setDatasetDetails(
          getDatasetDetails({
            owner_username,
            min_date,
            max_date,
          }),
        );
        if (default_cip) {
          dispatch(storeDefaultInformation(default_cip));
        }

        if (chemical_selection) {
          dispatch(storeTrainChemicalSelection(chemical_selection));
        }

        if (file_id && uploaded_in_blob) {
          setColumnOptions([]);
          const columns = await getSheetColumns(file_id, sel_train);
          if (columns && columns.data) {
            setColumnOptions(columns.data?.cols);
          }
        }
        setColMappingType(col_mapping_type);

        // reset mandatory components status if selected file deleted
        if (plant_file_id === null) {
          setMandatoryComponentsStatus(initialMandatoryComponentsStatus);
        }
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPlantReadinessStatus = useCallback(async () => {
    if (!token) {
      setPlantReadinessStatus([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getPlantReadiness();
      setPlantReadinessStatus(response?.data || []);
    } catch {
      setPlantReadinessStatus([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, setIsLoading]);

  const handleRefreshTrainTabs = () => {
    getPlantDetail();
    refreshPlantReadinessStatus();
  };

  useEffect(() => {
    if (selectedTrain === undefined || selectedTrain === null) {
      setSelectedTrain(1);
    }
  }, [selectedTrain]);

  useEffect(() => {
    refreshPlantReadinessStatus();
  }, [selectedTrain, fileSheetMapping, refreshPlantReadinessStatus]);

  useEffect(() => {
    dispatch(storeApp(null));
    if (token) getPlantDetail();
  }, [token, fileSheetMapping]);

  useEffect(() => {
    if (showRecurrent) {
      setHasRecurrentCIPs(true);
    }
  }, [showRecurrent]);

  useEffect(() => {
    if (!fileId) {
      setDatasetDetails(
        getDatasetDetails({
          owner_username: ownerUserName,
        }),
      );
    }
  }, [fileId, ownerUserName]);

  useEffect(() => {
    return () => {
      removeLocalStorageItem("prevTrainConfig");
    };
  }, []);

  useUnsavedChangesBlocker(hasUnsavedChanges);

  const hasUploadedFile =
    Array.isArray(uploadedFiles) && uploadedFiles.length > 0;

  return (
    <div className="container-main">
      <Header showPlantMenu />
      <div className="main-container analyse-data-main-container">
        <div className="content-container descriptionText font-14 analyse-data-content">
          {activeLoadingRequests > 0 && (
            <div className="circular-loader-overlay">
              <CircularLoader />
            </div>
          )}
          <div
            className={`analyse-data-panels ${
              !hasUploadedFile ? "analyse-data-panels--empty" : ""
            }`}
          >
            <div className="analyse-data-panel analyse-data-panel--left">
              <div className="analyse-data-left-panel-parent">
                <LeftPanel
                  fileInfo={fileInfo}
                  uploadedFiles={uploadedFiles}
                  totalTrainCount={totalTrainCount}
                  selectedTrain={selectedTrain}
                  sheetNames={sheetNames}
                  allTrainsStatus={mergedTrainReadiness}
                  setTempSelectedTrain={setTempSelectedTrain}
                  onRefreshTrainTabs={handleRefreshTrainTabs}
                />
              </div>
            </div>

            <div className="analyse-data-panel analyse-data-panel--right">
              <TrainTabsSection
                config={config}
                selectedTrain={selectedTrain}
                hasUnsavedChanges={hasUnsavedChanges}
                trainReadinessMap={trainReadinessMap}
                plantId={plantId}
                tempSelectedTrain={tempSelectedTrain}
                trainTabActions={trainTabActions}
              />
              {hasUploadedFile ? (
                <>
                  <FileSheetSelection
                    fileInfo={fileInfo}
                    uploadedFiles={uploadedFiles}
                    config={config}
                    setDatasetDetails={setDatasetDetails}
                    setSelectedTrain={setSelectedTrain}
                    selectedTrain={selectedTrain}
                    selectedSheet={selectedSheet}
                    sheetNames={sheetNames}
                    setFileId={setFileId}
                    setMappedColumn={setMappedColumn}
                    setColumnOptions={setColumnOptions}
                    setMappedReference={setMappedReference}
                    setSelectedSheet={setSelectedSheet}
                    setSelectedCIPFileName={setSelectedCIPFileName}
                    setHasRecurrentCIPs={setHasRecurrentCIPs}
                    setSheetNames={setSheetNames}
                    setIsLoading={setIsLoading}
                    setIsAppendFile={setIsAppendFile}
                    setAppendFileList={setAppendFileList}
                    setAppendStatus={setAppendStatus}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setNormSelectionType={setNormSelectionType}
                    setColMappingType={setColMappingType}
                    setSelectedRadioColumn={setSelectedColumnRadioColumn}
                    setUploadedFiles={setUploadedFiles}
                    setFileInfo={setFileInfo}
                  />
                  <div className="flex-ai-center dataset-name">
                    <span className="tealText">
                      {t("analyseData.datasetName")}{" "}
                    </span>
                    &nbsp;
                    {datasetDetails}
                  </div>

                  <ColumnSelection
                    columnOptions={columnOptions}
                    setColumnOptions={setColumnOptions}
                    fileId={fileId}
                    isEdit={isEdit}
                    mappedColumn={mappedColumn}
                    setMappedColumn={setMappedColumn}
                    mappedReference={mappedReference}
                    setTempReferenceValues={setTempReferenceValues}
                    setDatasetDetails={setDatasetDetails}
                    selectedRadioColumn={selectedColumnRadioColumn}
                    setSelectedRadioColumn={setSelectedColumnRadioColumn}
                    setMappedReference={setMappedReference}
                    setIsNormalizationColumnFilled={
                      setIsNormalizationColumnFilled
                    }
                    config={config}
                    setSelectedTrain={setSelectedTrain}
                    selectedTrain={selectedTrain}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                    normSelectionType={normSelectionType}
                    ownerUserName={ownerUserName}
                    plantReadinessStatus={plantReadinessStatus}
                    colMappingType={colMappingType}
                    uploadedInBlob={fileInfo?.uploaded_in_blob}
                  />

                  <DataAppend
                    title={t("dataAppend.title")}
                    content={t("dataAppend.content")}
                    buttonText={t("dataAppend.appendData")}
                    linkText={t("dataAppend.linkText")}
                    selectedTrain={selectedTrain}
                    fileId={fileId}
                    fileName={fileInfo?.fileName}
                    setIsLoading={setIsLoading}
                    mappedReference={mappedReference}
                    appendFileList={appendFileList}
                    isAppendFile={isAppendFile}
                    appendStatus={appendStatus}
                    setIsAppendFile={setIsAppendFile}
                    setAppendFileList={setAppendFileList}
                    setAppendStatus={setAppendStatus}
                    setTempReferenceValues={setTempReferenceValues}
                  />

                  <ReferenceCondition
                    fileId={fileId}
                    isEdit={isEdit}
                    mappedReference={mappedReference}
                    tempReferenceValues={tempReferenceValues}
                    selectedColumnRadioColumn={selectedColumnRadioColumn}
                    selectedTrain={selectedTrain}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                  />

                  <DefaultCIPInformation
                    selectedTrain={selectedTrain}
                    defaultCIPKeys={DEFAULT_CIP_KEYS}
                    setMandatoryComponentsStatus={setMandatoryComponentsStatus}
                    noOfRONFElements={mappedColumn?.sel_element_involved}
                    selectedPass={mappedColumn?.sel_pass}
                    selectedStage={mappedColumn?.sel_stage}
                  />

                  <CIPSpecification
                    selectedTrain={selectedTrain}
                    selectedSheet={selectedSheet}
                    selectedCIPFileName={selectedCIPFileName}
                    hasRecurrentCIPs={hasRecurrentCIPs}
                    shouldScrollToRecurrent={showRecurrent}
                    mappedReference={mappedReference}
                  />
                </>
              ) : (
                <EmptyStateContent />
              )}
            </div>
          </div>
        </div>

        {isSettingsDialogOpen && (
          <AdvancedSettings
            outlierRemoval
            handleClose={handleSettingsDialogClose}
            selectedTrain={selectedTrain}
            cipAndShutdownIdentification
          />
        )}
        {showPlantChecklist && (
          <PlantChecklist
            setShowPlantChecklist={setShowPlantChecklist}
            checkListData={plantChecklistData}
          />
        )}
        {isFTNormFallbackDialogOpen && (
          <ConfirmationPopup
            header={t("analyseData.dataNormalizationForAnalysis")}
            description={t("analyseData.ftNormFallbackMessage")}
            confirmBtn={t("ValueStrings.okay")}
            onClose={handleFTNormFallbackClose}
            hideCancel
            type="info"
            width="500px"
          />
        )}
        <div className="flex-between analyse-data-actions">
          <WPTertiaryButton
            customClass="margin-left-10"
            onClick={handleSettingsDialogOpen}
            label={t("analyseData.settings")}
            disabled={!hasUploadedFile}
            icon={<SettingsIcon />}
            iconPlacement="before"
            size="medium"
            className="margin-right-20"
            variant="outlined"
          />

          <div>
            <WPTertiaryButton
              id="back-to-plant-design-btn"
              label={t("analyseData.backtoPlantDesign")}
              onClick={onExitClick}
              icon={<ArrowLeftIcon />}
              iconPlacement="before"
              size="medium"
              className="margin-right-20"
              variant="outlined"
            />
            <WPTertiaryButton
              onClick={onProceedClick}
              label={t("analyseData.proceedtoAnalysis")}
              disabled={!hasUploadedFile}
              icon={<ArrowRightIcon />}
              iconPlacement="after"
              variant="contained"
              type="secondary"
              size="medium"
            />
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AnalyseData;
